import { Input, Typography, CloseAltIcon, CheckIcon } from '@mgmresorts/wcl-lab';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useContent } from '../../../hooks/useContent';
import { useEvent } from '../../../utils/analytics/useEvent';
import { MaxLengths } from '../../../utils/constants';
import { SignUpFormContext } from '../SignUpFormContext';

export function PasswordInput() {
  const trackEvent = useEvent();
  const { password, isLoading, bindPassword, invalidFields, passwordValidations, isValidPassword } =
    useContext(SignUpFormContext);
  const { getContentByPath } = useContent();
  const [pwError, setPWError] = useState('');
  const successColor = '#00803C';

  useEffect(() => {
    if (invalidFields.includes('password')) {
      const message = getContentByPath('accountCreation.fields.password.empty') as string;
      if (pwError !== message) {
        trackEvent(
          'identity – mlife - registration input error',
          'mlife registration input error',
          'mlifereginputerror',
          {
            errorField: getContentByPath('accountCreation.fields.password.title'),
            errorDisplayed: message,
          },
        );
        setPWError(message);
      }
    } else if (password && !isValidPassword) {
      const message = getContentByPath('accountCreation.fields.password.policy') as string;
      if (pwError !== message) {
        trackEvent(
          'identity – mlife - registration input error',
          'mlife registration input error',
          'mlifereginputerror',
          {
            errorField: getContentByPath('accountCreation.fields.password.title'),
            errorDisplayed: message,
          },
        );
        setPWError(message);
      }
    } else {
      setPWError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContentByPath, invalidFields, trackEvent]);

  const getPasswordFieldError = useCallback(() => {
    if (invalidFields.includes('password')) {
      return getContentByPath('accountCreation.fields.password.empty');
    }

    if (!isValidPassword) {
      return getContentByPath('accountCreation.fields.password.policy');
    }

    return '';
  }, [invalidFields, isValidPassword, getContentByPath]);

  const error = useMemo(
    () => (invalidFields.includes('password') || (password && !isValidPassword) ? getPasswordFieldError() : undefined),
    [getPasswordFieldError, invalidFields, isValidPassword, password],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      let color = '#E02D21';
      let Icon = CloseAltIcon;
      if (!password) {
        color = '#5D5D5D';
      } else if (isValid) {
        color = successColor;
        Icon = CheckIcon;
      }
      return (
        <li className='identity__password-hint'>
          {password && <Icon color={color} size={20} width={40} />}
          <Typography variant='small' color={color}>
            {hint}
          </Typography>
        </li>
      );
    },
    [passwordValidations, password],
  );

  return (
    <>
      <Input
        type='password'
        testId='password'
        label={getContentByPath('accountCreation.fields.password.title')}
        id='password'
        aria-label={getContentByPath('accountCreation.fields.password.label')}
        className='identity__field identity__field-password'
        error={!!error}
        hint={error as string}
        {...bindPassword}
        disabled={isLoading}
        maxLength={MaxLengths.Password}
      />
      <ul
        className={`identity__password-hints-wrapper ${password ? 'identity__password-hints-wrapper-non-empty' : ''}`}
      >
        <>
          {(!password || error) && (
            <>
              {passwordValidationHint('length', getContentByPath('accountCreation.fields.password.lengthHint'))}
              {passwordValidationHint('number', getContentByPath('accountCreation.fields.password.numberHint'))}
              {passwordValidationHint('uppercase', getContentByPath('accountCreation.fields.password.uppercaseHint'))}
              {passwordValidationHint('lowercase', getContentByPath('accountCreation.fields.password.lowercaseHint'))}
              {passwordValidationHint(
                'specialCharacter',
                getContentByPath('accountCreation.fields.password.specialCharacterHint'),
              )}
              {password.length > 0 &&
                passwordValidationHint('firstName', getContentByPath('accountCreation.fields.password.firstName'))}
              {password.length > 0 &&
                passwordValidationHint('lastName', getContentByPath('accountCreation.fields.password.lastName'))}
            </>
          )}
          {password && !error && (
            <li className='identity__password-hint'>
              <CheckIcon size={20} width={40} color={successColor} />
              <Typography variant='small' color={successColor}>
                {getContentByPath('accountCreation.fields.password.allMet')}
              </Typography>
            </li>
          )}
        </>
      </ul>
    </>
  );
}

PasswordInput.displayName = 'PasswordInputField';
